import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@els/els-react--checkbox';
import { SingleSelect } from '@els/els-react--select';
import AutosuggestInput from '@Components/AutosuggestInput';
import Button from '@Components/Button';
import LoginFormHeader from '../../Components/LoginFormHeader';

import './InstitutionForm.scss';
import '../../Components/LoginForm.scss';

function InstitutionForm({
  cancel,
  actualData,
  selectGroup,
  login,
  institutionsGroups,
  institutionsForSearch,
  goToLastSelected,
  ipAddress,
  isAnonymousUser,
  selectInstitution,
  selectSearchedInstitution,
  lastSelectedInstitution,
  changeRememberMe,
  loginFail,
  isLoading,
  institutionErrorMessage,
}) {
  const [searchForInstitution, setSearchForInstitution] = useState('');
  const { currentInstitutions, group, institution, rememberChoice } = actualData;
  const onChangeRememberMe = (e) => changeRememberMe(e.target.checked);

  const mapDropdownItems = (items) => {
    if (!items || !items.length) {
      return [];
    }
    return items.map((item) => ({
      label: item.description,
      value: item.description,
    }));
  };

  const mapDropdownSubItems = (items) => {
    if (!items || !items.length) {
      return [];
    }
    return items.map((item, index) => ({
      label: item.description,
      value: index,
    }));
  };

  const getData = () => {
    if (!institutionsForSearch || !institutionsForSearch.length) {
      return [];
    }
    const data =
      institutionsForSearch &&
      institutionsForSearch.filter((item) => {
        const description = item.description.toLowerCase();
        return description.includes(searchForInstitution.toLowerCase());
      });

    return data
      .map((item) => ({
        title: item.description,
        value: item.description,
      }))
      .slice(0, 5);
  };

  const mappedInstitutionsGroups = mapDropdownItems(institutionsGroups);
  const mappedCurrentInstitutions = mapDropdownSubItems(currentInstitutions);

  const onSelectInstitutionGroup = ({ value }) => {
    if (!institutionsGroups || !institutionsGroups.length) {
      return;
    }
    const selectedGroup = institutionsGroups.find((item) => item.description === value);
    selectGroup(selectedGroup);
  };

  const onSelectInstitution = ({ value }) => {
    if (!currentInstitutions || !currentInstitutions.length) {
      return;
    }
    const selectedInstitution = currentInstitutions.findIndex((item, index) => index === value);
    selectInstitution(currentInstitutions[selectedInstitution].description);
  };

  const onSelectSearchedInstitution = (selectedInstitution) => {
    if (!institutionsForSearch || !institutionsForSearch.length) {
      return;
    }
    const selectedSearchedInstitution = institutionsForSearch.find((item) => item.description === selectedInstitution.value);
    selectSearchedInstitution(selectedSearchedInstitution);
  };

  const onLastSelectedInstitutionClick = () => {
    goToLastSelected(lastSelectedInstitution.callbackUrl);
  };

  return (
    <div className="login-form institutional-form login-page-form-wrapper">
      <LoginFormHeader
        cancel={cancel}
        isAnonymousUser={isAnonymousUser}
        ipAddress={ipAddress}
        actionMessage="Institution sign in"
        infoMessage="Sign in via your institution"
      />

      {lastSelectedInstitution && (
        <div className="institutional-form__last-selected-institution">
          <span>Previously selected: </span>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <a className="u-els-color-link-inline" onClick={onLastSelectedInstitutionClick}>
            {lastSelectedInstitution.institution}
          </a>
        </div>
      )}

      <div className="institutional-form__search-input" id="els-input-wrapper">
        <AutosuggestInput
          animatedPlaceholder
          placeholder="Search for your institution"
          getData={getData}
          clearButton={false}
          idInput="id-institution-search-input"
          classNameInput="new-input-input-with-clear-button"
          value={searchForInstitution}
          onChange={setSearchForInstitution}
          onSelect={onSelectSearchedInstitution}
          debounceDelay={0}
          groupBy="category"
          highlight
          autofocus
          minSymbolsToStart={1}
          afterLabelContent={
            <Button
              type="button"
              buttonConfig={{
                type: 'link',
                size: 'sm',
                ariaLabel: 'Institution search',
                iconOnly: true,
                className: 'institutional-form__search-input_icon',
                e2eName: 'institution-search-input',
              }}
              iconConfig={{
                name: 'search',
                size: 'tiny',
              }}
            />
          }
        />
      </div>

      <div className="institutional-form__separator">
        <span>OR</span>
      </div>

      <div className="institutional-form__dropdown u-els-margin-top">
        <SingleSelect
          id="institution-group-dropdown"
          className="e2e-institution-group-dropdown"
          options={mappedInstitutionsGroups}
          selectedOptions={group ? [group] : undefined}
          label="Select your region or group"
          optionsFlyoutProps={{
            maxHeight: '10rem',
          }}
          placeholderText="Select your region or group"
          onSelect={onSelectInstitutionGroup}
          size="small"
          required
          shouldHideLabel
        />
      </div>

      {currentInstitutions && currentInstitutions.length ? (
        <div className="institutional-form__dropdown">
          <SingleSelect
            id="institution-institution-dropdown"
            className="e2e-institution-institution-dropdown"
            options={mappedCurrentInstitutions}
            optionsFlyoutProps={{
              maxHeight: '10rem',
            }}
            selectedOptions={institution ? [institution] : undefined}
            label="Select your institution"
            placeholderText="Select your institution"
            onSelect={onSelectInstitution}
            size="small"
            required
            shouldHideLabel
          />
        </div>
      ) : null}

      {!isLoading && loginFail && <div className="login-form__content_error u-els-margin-bottom">{institutionErrorMessage}</div>}

      <div className="els-text-input">
        <div className="login-form__content_remember institutional-form__remember">
          <Checkbox
            checked={rememberChoice}
            id="institution_remember"
            onChange={onChangeRememberMe}
            label="Remember me on this computer"
            shouldHideLabel
          />
          <div className="login-form__content_remember-text">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="institution_remember" title="Remember me on this computer">
              <span>Remember me on this computer</span>
            </label>
          </div>
        </div>
      </div>

      <div className="login-form__content_actions">
        <div className="login-form__content_actions-button">
          <Button
            buttonConfig={{
              type: 'primary',
              size: 'md',
              label: 'Go',
              e2eName: 'institution-sign-in',
            }}
            iconConfig={{
              name: 'chevron-right',
              size: 'sm',
            }}
            onClick={login}
          />
        </div>
      </div>
    </div>
  );
}

InstitutionForm.defaultProps = {
  actualData: {},
  institutionsGroups: [],
  institutionsForSearch: [],
};

InstitutionForm.propTypes = {
  isAnonymousUser: PropTypes.bool,
  cancel: PropTypes.func.isRequired,
  ipAddress: PropTypes.string,
  loginFail: PropTypes.string,
  actualData: PropTypes.object,
  selectGroup: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  institutionsGroups: PropTypes.array,
  institutionsForSearch: PropTypes.array,
  goToLastSelected: PropTypes.func.isRequired,
  selectInstitution: PropTypes.func.isRequired,
  selectSearchedInstitution: PropTypes.func.isRequired,
  lastSelectedInstitution: PropTypes.object,
  changeRememberMe: PropTypes.func.isRequired,
  institutionErrorMessage: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default InstitutionForm;
